import {ApolloError } from '@apollo/client/core';

import {  GraphQLFormattedError } from 'graphql';

export interface DataError{
    listIndex: number;
    message: string;
    code?: string;
    property?: string;
  }

/**
 *
 * @param errors List of the errors returned by the query
 * @param rootQuery The name of the root query, used in the case that two queries are run in one call
 * @param pathIndex The error path level that conatins the list index value for the error
 */
export function parseGraphqlErrors(errors: readonly GraphQLFormattedError[], rootQuery, indexLevel = 2): DataError[] {
    // Return an empty array if the query has no errors
    if (!errors?.length) { return []; }

    // Filter out errors from other queries in the same call
    const filteredErrors = errors.filter((err) => err.path[0] === rootQuery);

    return filteredErrors.map((err) => ({
        listIndex: err.path[indexLevel],
        message: err.message,
        code: err?.extensions?.code,
        property: err?.path[indexLevel + 1] || undefined,
    } as DataError));
}

export function getApolloErrorMessage(error: ApolloError) {
    //TO DO improve this logic to handle protocolErrors and clientErrors as well
    const { graphQLErrors, message } = error;

    if (graphQLErrors?.length > 0) {
      return graphQLErrors[0].message;
    }

    const networkErrors = getNetworkErrors(error);

    if (networkErrors.length > 0) {
      return networkErrors[0].message;
    }

    return message;
}

function getNetworkErrors(error: ApolloError) {

    if (error.networkError == null) { return []; }

    if (
      'result' in error.networkError &&
      typeof error.networkError.result !== 'string' &&
      Array.isArray(error.networkError.result.errors)
    ) {
      return error.networkError.result.errors;
    } else if (
      'error' in error.networkError &&
      typeof error.networkError.error === 'object' &&
      'errors' in error.networkError.error &&
      Array.isArray(error.networkError.error.errors)
    ) {
      return error.networkError.error.errors;
    }

    return [];
}

/**
 * Machine-readable code the backend attaches when a report is killed by the interactive Neo4j
 * transaction timeout. Must match `REPORT_TIMEOUT_ERROR_TYPE` in the backend's
 * `dynamicReports/previewReport.types.ts`.
 */
export const REPORT_TIMEOUT_CODE = 'REPORT_TIMEOUT';

/**
 * The ingress returns this when a request outlives its `proxy-read-timeout`. The backend's own cap
 * is set below that threshold so the typed error normally wins the race, but this stays reachable
 * whenever pre-query work (auth, permissions, connection acquisition) is slow enough to push the
 * request past the ingress limit before the transaction timeout fires.
 */
const GATEWAY_TIMEOUT_STATUS = 504;

// Requirement: a timed-out report must be recognised whether it arrives as our typed GraphQL error
// or as the ingress's bare 504 — both mean the same thing to the user, so both get the same copy.
export function isReportTimeoutError(error: ApolloError): boolean {

    if (!error) { return false; }

    if (isReportTimeoutGraphqlError(error.graphQLErrors)) { return true; }

    const status = (error.networkError as { statusCode?: number } | null)?.statusCode;

    return status === GATEWAY_TIMEOUT_STATUS;
}

/**
 * Same check for the `errorPolicy: 'all'` path, where GraphQL errors arrive on the query result
 * rather than as a thrown `ApolloError`.
 */
export function isReportTimeoutGraphqlError(errors: readonly GraphQLFormattedError[]): boolean {
    return Boolean(errors?.some((err) => err?.extensions?.code === REPORT_TIMEOUT_CODE));
}

/**
 * Full dashboard copy for a timed-out widget: what happened, then what to do.
 *
 * The dashboard is powered by dynamic reports internally, but "Reports" names a different feature in
 * the UI, so the backend's report-centric message is deliberately not reused on this surface — the
 * dashboard owns its own wording end to end.
 *
 * Phrased as "Some data" rather than "This data" because the same string serves both the per-widget
 * tooltip and the shared banner, which is deduplicated and may stand in for several failed widgets.
 */
// Requirement: never say "report" in dashboard copy — it names a different feature in the UI.
// Requirement: lead the advice with zone breadth. The P1 that motivated the timeout was a national
// (`ymm-nsc`) dashboard load fanning out across every child zone, so narrowing the period alone
// would not have saved it. The dashboard has no filter builder, hence no "add filters" advice.
const REPORT_TIMEOUT_DASHBOARD_MESSAGE =
    'Some data took too long to load and was stopped. '
    + 'Try a single zone instead of a parent zone, or a shorter period.';

/**
 * "What happened" sentence for a timeout we only recognised from the ingress's 504, where there is
 * no backend message to quote — the raw Apollo text would read "Received status code 504".
 *
 * Intentionally identical to the backend's `REPORT_TIMEOUT_MESSAGE`, so the user sees the same
 * wording whichever way the timeout reached us. Update both together.
 */
export const REPORT_TIMEOUT_FALLBACK_FACT =
    'This report was stopped because it took too long to run.';

/**
 * Remediation for the dynamic report builder, which — unlike the dashboard — has filters and a
 * save/export path. Export runs outside the interactive boundary and so keeps the generous
 * server-side limit, making it genuine advice for a report that legitimately needs every row.
 */
export const REPORT_TIMEOUT_PREVIEW_ADVICE =
    'Try a single zone instead of a parent zone, a shorter date range, or more filters. '
    + 'For a full report, save it and use Export.';

/**
 * Toast detail for a timed-out report: the backend states what happened, and the caller supplies
 * advice for its own surface. Used by the dynamic report builder, where "report" is the right noun;
 * the dashboard substitutes {@link REPORT_TIMEOUT_DASHBOARD_MESSAGE} instead.
 *
 * Accepts the GraphQL errors from either source — `ApolloError.graphQLErrors` or an
 * `errorPolicy: 'all'` query result — and falls back to {@link REPORT_TIMEOUT_FALLBACK_FACT} when
 * the timeout was only visible as a 504 and there is no typed error to quote.
 */
export function getReportTimeoutDetail(
    errors: readonly GraphQLFormattedError[] | undefined,
    advice: string,
): string {

    const timeoutError = errors?.find((err) => err?.extensions?.code === REPORT_TIMEOUT_CODE);

    return `${ timeoutError?.message || REPORT_TIMEOUT_FALLBACK_FACT } ${ advice }`;
}

/**
 * Error text for a dashboard widget. Timeouts get the dashboard's own copy — a constant, so that
 * when several widgets time out together the store's `uniq` collapses them into a single banner
 * instead of repeating it per widget. Every other failure keeps the raw message.
 */
export function getDashboardErrorMessage(error: ApolloError): string {

    if (isReportTimeoutError(error)) {
      return REPORT_TIMEOUT_DASHBOARD_MESSAGE;
    }

    return getApolloErrorMessage(error);
}

/**
 * One extension member off the first GraphQL error, when there is one.
 *
 * `application/problem+json` codes reach the browser as GraphQL error
 * extensions, and several of them carry the figure needed to RECOVER from the
 * refusal rather than merely report it — `partial_refund_unavailable` hands
 * back the exact full-refund amount, which is what turns a dead end into one
 * click. Reading that is a general concern, not a feature's, so it lives here
 * beside the other error readers.
 *
 * Returns `undefined` rather than throwing on any shape it does not recognise:
 * the code list is OPEN by contract, so an unfamiliar error must degrade, never
 * crash the surface that was trying to explain it.
 */
export function getGraphqlErrorExtension(error: unknown, key: string): unknown {
	const extensions = (error as { graphQLErrors?: { extensions?: Record<string, unknown> }[] })
		?.graphQLErrors?.[0]?.extensions;

	return extensions?.[key];
}

export const DOCUMENT_REFRESH_REFUSED_CODE = 'DOCUMENT_REFRESH_REFUSED';

export function isDocumentRefreshRefusedError(error: ApolloError): boolean {
    return Boolean(error?.graphQLErrors?.some((err) => err?.extensions?.code === DOCUMENT_REFRESH_REFUSED_CODE));
}
